import { View, Text, Image } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';

/**
 * A company's logo, falling back to its initials.
 *
 * Exists because the logo was being extracted from the deck, re-hosted in the
 * public `company-logos` bucket and served correctly — and then shown almost
 * nowhere. `logo_url` lives on `company_directory`, which only the directory
 * screen read, so every dealflow card rendered initials for a company whose
 * logo was sitting one join away.
 *
 * Callers pass `logoUrl` from `StartupSummary.company.logoUrl`. `undefined`
 * (not loaded on this path) and `null` (no logo) both render initials, so a
 * screen that has not been wired up degrades to the old behaviour rather than
 * to a broken image.
 */
export function CompanyAvatar({
  name,
  logoUrl,
  isKnockout = false,
  size = 40,
  radius = 8,
}: {
  name: string;
  logoUrl?: string | null;
  /**
   * The logo is a near-white "knockout" and needs a dark chip behind it.
   *
   * Without this it renders white-on-teal and simply disappears — which is what
   * Octotronic's wordmark did on the dealflow card. The extractor has always
   * computed the fact; it was dropped at the upload boundary until
   * `company_directory.logo_is_knockout` existed to carry it here.
   */
  isKnockout?: boolean;
  size?: number;
  radius?: number;
}) {
  const colors = useThemeColors();

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <View
      // overflow-hidden clips the logo to the rounded corners — logos are
      // frequently square PNGs with their own background.
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        // A knockout logo is white-on-transparent: on the normal tinted chip it
        // vanishes. Chosen from the theme rather than baked into the PNG at
        // extraction, so it adapts to light/dark mode and can be changed without
        // a data migration or re-extracting every logo.
        backgroundColor: isKnockout ? colors.dark[800] : colors.primary[100],
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {logoUrl ? (
        <Image
          source={{ uri: logoUrl }}
          // `contain`, not `cover`: a logo cropped to fill loses its wordmark.
          resizeMode="contain"
          style={{ width: size, height: size }}
          accessibilityLabel={`${name} logo`}
        />
      ) : (
        <Text
          style={{ color: colors.primary[700], fontSize: size * 0.35 }}
          className="font-bold"
        >
          {initials}
        </Text>
      )}
    </View>
  );
}
